import { useState, useEffect, useRef, useCallback } from "react";
import { collection, getDocs, query, orderBy, limit } from "firebase/firestore";
import { useLanguage } from "../context/LanguageContext";
import { db } from "../firebase";
import { Link } from "react-router-dom";
import { sectionPath } from "../utils/routePaths";
import { resolveSlug } from "../utils/slugify";
import { optimizedImageUrl } from "../utils/cloudinaryUrl";

const ACCENT = "#C47C2B";
const DARK = "#1A1A18";

const stripHtml = (html) => {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();
};

function FeaturedCard({ place, language, onDragStart }) {
  const [hovered, setHovered] = useState(false);
  const mouseDownX = useRef(0);
  const name = language === "ka" && place.nameKa ? place.nameKa : place.name;
  const desc = stripHtml(language === "ka" && place.descriptionKa ? place.descriptionKa : place.description);
  const season = place.bestTimeToVisit
    ? Object.entries(place.bestTimeToVisit).sort((a, b) => (b[1].rating || 0) - (a[1].rating || 0))[0]?.[0]
    : null;
  const seasonLabel = { spring: "Spring", summer: "Summer", autumn: "Autumn", winter: "Winter" }[season] || "All Year";

  return (
    <Link
      to={sectionPath(language, "place", resolveSlug(place, language))}
      draggable={false}
      style={{
        flexShrink: 0, width: 280, height: 220, borderRadius: 16,
        overflow: "hidden", display: "block", position: "relative",
        background: DARK, textDecoration: "none",
        transform: hovered ? "translateY(-3px)" : "translateY(0)",
        transition: "transform 0.3s, box-shadow 0.3s",
        boxShadow: hovered ? "0 8px 24px rgba(0,0,0,0.14)" : "0 2px 8px rgba(0,0,0,0.08)",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onMouseDown={(e) => {
        mouseDownX.current = e.clientX;
        onDragStart(e);
      }}
      onClick={(e) => {
        if (Math.abs(e.clientX - mouseDownX.current) > 5) {
          e.preventDefault();
        }
      }}
    >
      {place.image && (
        <img src={optimizedImageUrl(place.image, 600)} alt={name} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", transform: hovered ? "scale(1.06)" : "scale(1)", transition: "transform 0.5s ease" }} />
      )}
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(10,8,6,0.88) 0%, rgba(10,8,6,0.3) 55%, rgba(10,8,6,0.1) 100%)" }} />
      <div style={{ position: "absolute", top: 14, left: 14, background: "rgba(26,26,24,0.65)", color: "#fff", padding: "4px 12px", borderRadius: 999, fontFamily: "Geist, sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.04em" }}>
        {season
          ? (language === "ka" ? { spring: "გაზაფხული", summer: "ზაფხული", autumn: "შემოდგომა", winter: "ზამთარი" }[season] : seasonLabel)
          : (language === "ka" ? "მთელი წელი" : "All Year")}
      </div>
      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "14px 16px", display: "flex", alignItems: "flex-end", justifyContent: "space-between" }}>
        <div style={{ flex: 1, marginRight: 10 }}>
          <h3 style={{ fontFamily: "Geist, sans-serif", fontSize: 18, fontWeight: 700, color: "#fff", marginBottom: 4, letterSpacing: "-0.01em" }}>{name}</h3>
          {desc && (
            <p style={{ fontFamily: "Geist, sans-serif", fontSize: 12, color: "rgba(255,255,255,0.65)", margin: 0, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", lineHeight: 1.5 }}>
              {desc}
            </p>
          )}
        </div>
        <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transform: hovered ? "scale(1.1)" : "scale(1)", transition: "transform 0.2s" }}>
          <span className="material-symbols-outlined" style={{ fontSize: 17, color: DARK }}>arrow_forward</span>
        </div>
      </div>
    </Link>
  );
}

export default function FeaturedPlaces() {
  const { language } = useLanguage();
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canScrollLeft,  setCanScrollLeft]  = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const scrollRef   = useRef(null);
  const isDragging  = useRef(false);
  const startX      = useRef(0);
  const scrollStart = useRef(0);
  const hasDragged  = useRef(false);

  useEffect(() => {
    getDocs(query(collection(db, "places"), orderBy("likes", "desc"), limit(8)))
      .then((snap) => setPlaces(snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((p) => p.image)))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  /* ── arrow visibility ── */
  const updateArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // Throttle to one geometry read per frame — reading scrollLeft/scrollWidth
    // on every raw "scroll" event (which fires continuously during drag) forces
    // a synchronous layout reflow instead of letting the browser batch it.
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => { updateArrows(); ticking = false; });
    };
    requestAnimationFrame(updateArrows);
    el.addEventListener("scroll", onScroll, { passive: true });
    const ro = new ResizeObserver(updateArrows);
    ro.observe(el);
    return () => { el.removeEventListener("scroll", onScroll); ro.disconnect(); };
  }, [places, loading, updateArrows]);

  const doScrollBy = (dir) => scrollRef.current?.scrollBy({ left: dir * 300, behavior: "smooth" });

  /* ── drag to scroll — stable refs so window listeners always see current values ── */
  const onMouseDown = useCallback((e) => {
    // only left button
    if (e.button !== 0) return;
    isDragging.current  = true;
    hasDragged.current  = false;
    startX.current      = e.clientX;
    scrollStart.current = scrollRef.current?.scrollLeft ?? 0;
    e.preventDefault();
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      if (!isDragging.current) return;
      const dx = e.clientX - startX.current;
      if (Math.abs(dx) > 3) hasDragged.current = true;
      if (scrollRef.current) {
        scrollRef.current.scrollLeft = scrollStart.current - dx;
      }
      document.body.style.cursor = "grabbing";
    };

    const onUp = () => {
      if (!isDragging.current) return;
      isDragging.current = false;
      document.body.style.cursor = "";
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup",   onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup",   onUp);
    };
  }, []); // empty deps — closures use refs so always fresh

  const onClickCapture = useCallback((e) => {
    if (hasDragged.current) {
      e.preventDefault();
      e.stopPropagation();
      hasDragged.current = false;
    }
  }, []);

  if (!loading && places.length === 0) return null;

  const SIDE_PAD = "6vw";

  return (
    <section style={{ background: "transparent", paddingTop: "clamp(40px, 5vw, 72px)", paddingBottom: "clamp(40px, 5vw, 72px)" }}>

      {/* Header */}
      <div style={{ padding: `0 ${SIDE_PAD}`, marginBottom: 24, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 8 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M2 22L10 8L14 14L18 10L22 22Z" stroke={ACCENT} strokeWidth="2.2" strokeLinejoin="round" fill="none"/>
            </svg>
            <span style={{ fontFamily: "Geist, sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--accent-text)" }}>
              {language === "ka" ? "ამ სეზონის საუკეთესო" : "Best Places This Season"}
            </span>
          </div>
          <h2 style={{ fontFamily: "Geist, sans-serif", fontSize: "clamp(22px, 2.8vw, 34px)", fontWeight: 700, letterSpacing: "-0.02em", color: "var(--text-primary)", margin: 0 }}>
            {language === "ka" ? "ამ სეზონში სასტუმრო ადგილები" : "Best places to visit this season"}
          </h2>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div className="fp-arrows" style={{ display: "flex", gap: 6 }}>
            <button onClick={() => doScrollBy(-1)} disabled={!canScrollLeft}
              style={{ width: 36, height: 36, borderRadius: "50%", border: "1.5px solid var(--border)", background: "var(--bg-surface)", display: "flex", alignItems: "center", justifyContent: "center", cursor: canScrollLeft ? "pointer" : "default", opacity: canScrollLeft ? 1 : 0.35, transition: "opacity 0.2s" }}>
              <span className="material-symbols-outlined" style={{ fontSize: 18, color: "var(--text-primary)" }}>arrow_back</span>
            </button>
            <button onClick={() => doScrollBy(1)} disabled={!canScrollRight}
              style={{ width: 36, height: 36, borderRadius: "50%", border: "1.5px solid var(--border)", background: "var(--bg-surface)", display: "flex", alignItems: "center", justifyContent: "center", cursor: canScrollRight ? "pointer" : "default", opacity: canScrollRight ? 1 : 0.35, transition: "opacity 0.2s" }}>
              <span className="material-symbols-outlined" style={{ fontSize: 18, color: "var(--text-primary)" }}>arrow_forward</span>
            </button>
          </div>

          <Link to={sectionPath(language, "categories")}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, border: "1.5px solid var(--border)", color: "var(--text-primary)", padding: "9px 18px", borderRadius: 8, fontFamily: "Geist, sans-serif", fontSize: 13, fontWeight: 600, textDecoration: "none", transition: "all 0.2s", whiteSpace: "nowrap" }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "var(--bg-inverse)"; e.currentTarget.style.color = "var(--fg-inverse)"; e.currentTarget.style.borderColor = "var(--bg-inverse)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "var(--text-primary)"; e.currentTarget.style.borderColor = "var(--border)"; }}>
            {language === "ka" ? "ყველა" : "View All"}
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>arrow_forward</span>
          </Link>
        </div>
      </div>

      {/* Scroll row
          - outer div: same padding as header → cards align with title & View All
          - inner div: overflow-x scroll, select: none during drag
          - paddingBottom on inner lets shadows bleed down without being cut */}
      <div style={{ paddingLeft: SIDE_PAD, paddingRight: SIDE_PAD }}>
        <div
          ref={scrollRef}
          className="fp-scroll"
          style={{
            display: "flex",
            gap: 14,
            overflowX: "auto",
            overflowY: "visible",
            scrollbarWidth: "none",
            WebkitOverflowScrolling: "touch",
            cursor: "grab",
            paddingTop: 10,
            paddingBottom: 28,
            userSelect: "none",
          }}
        >
          {loading
            ? Array(4).fill(0).map((_, i) => (
                <div key={i} style={{ flexShrink: 0, width: 280, height: 220, borderRadius: 16, background: "var(--bg-surface-alt)", animation: "fpShimmer 1.5s infinite" }} />
              ))
            : places.map((place) => (
    <FeaturedCard key={place.id} place={place} language={language} onDragStart={onMouseDown} />
  ))
          }
        </div>
      </div>

      <style>{`
        @keyframes fpShimmer { 0%,100%{opacity:.6} 50%{opacity:1} }
        .fp-scroll::-webkit-scrollbar { display: none; }
        .fp-scroll > * { scroll-snap-align: start; }
        @media (max-width: 600px) {
          .fp-arrows { display: none !important; }
        }
      `}</style>
    </section>
  );
}
